import { defineStore } from 'pinia'
import { ENV_DEV_OR_STAGING } from '@/helpers/environment'
import {
	DOMAINS_PREFIXES,
	getDefaultAppAreaConfigByHostname,
	defineFunnelByParams,
	getCrmRetargetingRouteNameBySlug,
} from '@/funnels/funnels.ts'
import { getPaywallRouteName } from '@/funnels/funnelUtils.ts'
import paywallRoutes from '@/modules/Paywall/paywallRoutes.ts'
import { useCoreFeaturesStore } from './coreFeatures'
import { useUserFlagsStore } from './userFlags'
import { useMainStore } from './main'
import { weeklyStorage, permanentStorage, sharedKeyPersist } from './persistence'

function groupSearchParamsByKey(entries) {
	return [...entries].reduce((result, tuple) => {
		const [key, val] = tuple
		if (result.hasOwnProperty(key)) {
			if (Array.isArray(result[key])) {
				result[key] = [...result[key], val]
			} else {
				result[key] = [result[key], val]
			}
		} else {
			result[key] = val
		}

		return result
	}, {})
}

function getFunnelPrefixFromPath(path) {
	const index = DOMAINS_PREFIXES.findIndex(([, regexp]) => regexp.test(path))

	if (index !== -1) {
		return DOMAINS_PREFIXES[index][0]
	}
	return '/'
}

function transformQueryParam(paramName, paramValue) {
	if (paramValue === '' || paramValue === 'true') {
		paramValue = true
	} else if (paramValue === 'false') {
		paramValue = false
	}

	if (paramName === 'email' && typeof paramValue === 'string') {
		paramValue = paramValue.trim().replaceAll(' ', '+')
	}

	return paramValue
}

const initialQueryParams = new URLSearchParams(window.location.search)

export const useLocationStore = defineStore('location', {
	state: () => ({
		host: window.location.hostname,
		queryParams: {},
		initialPath: '',
		paywallRoute: '',
		initialUtmParams: {},
		appAreaConfig: null,
		initialFunnelType: null,
		initialUserTags: null,
	}),

	getters: {
		hostname() {
			let hostname = this.host
			if (ENV_DEV_OR_STAGING || this.host === 'funnel-prod.workers.fstr.app') {
				hostname = this.getQueryParam('host') ?? 'simple-life-app.com'
			}
			return hostname
		},

		hostnameSimpleLife() {
			return this.hostname.includes('simple.life')
		},

		basePath() {
			return this.hostnameSimpleLife ? '/survey/' : '/'
		},

		getPathname() {
			return () => window.location.pathname.replace(this.basePath, '/')
		},

		getQueryParam: (state) => (param) => {
			let method = 'get'
			if (param === 'exp') {
				method = 'getAll'
			}
			let value = transformQueryParam(param, initialQueryParams[method](param))
			if (Array.isArray(value) && value.length === 0) {
				value = null
			}
			return value ?? state.queryParams[param]
		},

		getRawQueryParam() {
			return (param) => {
				const value = this.getQueryParam(param)
				if (typeof value === 'boolean') return null
				if (Array.isArray(value)) return value[0] ?? null
				return value
			}
		},

		isPaidTraffic() {
			return Boolean(this.getQueryParam('utm_source'))
		},

		isCrmAutologin() {
			return Boolean(
				this.getQueryParam('email') && this.getQueryParam('user') && this.getQueryParam('from') === 'email',
			)
		},

		isInAppAutologin() {
			return Boolean(
				this.getQueryParam('email') &&
				this.getQueryParam('user') &&
				(this.getQueryParam('from') === 'app' || this.getQueryParam('from') === 'inapp'),
			)
		},

		isCallAutologin() {
			return Boolean(this.getQueryParam('from') === 'call')
		},

		manageSubscriptionSource: (state) => state.queryParams.manage_subscription_source ?? 'web',

		/**
		 * @return {{web: boolean, android: boolean, ios: boolean}}
		 */
		accountPlatform() {
			return {
				ios: this.manageSubscriptionSource === 'ios',
				android: this.manageSubscriptionSource === 'android',
				web: this.manageSubscriptionSource !== 'ios' && this.manageSubscriptionSource !== 'android',
			}
		},

		getAppAreaConfig() {
			return this.appAreaConfig || getDefaultAppAreaConfigByHostname(this.hostname)
		},

		getPaywallRoute() {
			const planQueryParam = this.getQueryParam('plan')

			if (planQueryParam && paywallRoutes.map((route) => route.name).includes(planQueryParam)) {
				return planQueryParam
			}

			return getPaywallRouteName(this.getAppAreaConfig)
		},

		getCrmRetargetingRouteNameBySlug() {
			return (slug, defaultRouteName) => {
				return getCrmRetargetingRouteNameBySlug(this.hostname, slug, defaultRouteName)
			}
		},

		getInitialPath(state) {
			return state.initialPath
		},

		isEmptyInitialUtmParams: (state) => {
			return Object.keys(state.initialUtmParams).length === 0
		},

		getInitialFunnelType: (state) => state.initialFunnelType,

		getInitialUserTags: (state) => state.initialUserTags,
	},

	actions: {
		storeQueryParams() {
			const params = groupSearchParamsByKey(initialQueryParams.entries())
			const normalizedParams = {}

			for (let param in params) {
				normalizedParams[param] = transformQueryParam(param, params[param])
			}

			this.queryParams = {
				...this.queryParams,
				...normalizedParams,
			}
		},

		setAppAreaConfig(appAreaConfig) {
			this.appAreaConfig = appAreaConfig
		},

		setInitialFunnelType(value) {
			this.initialFunnelType = value
		},

		setInitialUserTags(value) {
			this.initialUserTags = value
		},

		storeInitialUtmParams(value) {
			this.initialUtmParams = value
		},

		removeQueryParam(key) {
			delete this.queryParams[key]
		},

		/*
			Method for checking if user came from organic traffic
			We are setting local flag to use it in strapi/in code to show changes based on it
			Its being set only once on app initialisation
			Organic utm sources and/or utm campaigns are defined in config builder
			https://simple-admin.fstr.app/config-builder/config/15
			As an example here is 5 cases we defined as "organic":
			https://simple-life-app.com/ is organic – w/o any utm sources
			https://simple-life-app.com/?utm_source=simple_landing – from simple.life main page
			https://simple-life-app.com/?utm_source=nb_blog - from simple.life blog
			https://simple-life-app.com/?utm_source=cta_blog - from simple.life blog
			https://simple-life-app.com/?utm_campaign=US_Simple_Search_Web_F_EP_Brand_220722 - from google search. In this case it can contain any utm_source
			Anything else is not considered "organic"
			https://simple-life-app.com/?utm_source=FB – is NOT organic, it's from ads on facebook
			https://simple-life-app.com/?utm_source=google – is NOT organic, it's from ads on google
			etc...
		 */
		defineOrganicTrafficFlag() {
			const coreFeaturesStore = useCoreFeaturesStore()
			const userFlagsStore = useUserFlagsStore()
			const utmSource = this.getQueryParam('utm_source')
			const utmCampaign = this.getQueryParam('utm_campaign')
			const manageSubscriptionSource = this.getQueryParam('manage_subscription_source')
			const organicUtmValues = coreFeaturesStore.FEAT_ORGANIC_UTM_VALUES
			let isOrganicUtmSource = false

			if (
				!utmSource ||
				organicUtmValues?.sources?.some((v) => utmSource?.toString().includes(v)) ||
				organicUtmValues?.campaigns?.some((v) => utmCampaign?.toString().includes(v))
			) {
				isOrganicUtmSource = true
			}

			if (manageSubscriptionSource) {
				isOrganicUtmSource = false
			}
			userFlagsStore.updateLocalFlags({ organicTrafficOnboarding: isOrganicUtmSource })
		},

		dismissWebView() {
			if (this.accountPlatform.ios) {
				window.location.href = 'fstr://simple/manage_subscription/dismiss_web_view'
			} else if (this.accountPlatform.android) {
				window.location.href = 'fstr://simple/settings_and_account_details'
			} else {
				window.open('https://simple.onelink.me/k7Ew', '_blank')
			}
		},

		storeInitialPath({ force = false, path } = {}) {
			const mainStore = useMainStore()
			if (!path) {
				path = this.getPathname()
			} else {
				path = path.replace(this.basePath, '/')
			}

			const prefix = getFunnelPrefixFromPath(path)
			const changeGenderFunnelPrefix = this.getInitialPath !== prefix && prefix !== '/'
			if (force || !this.getInitialPath || changeGenderFunnelPrefix) {
				const appAreaConfig = defineFunnelByParams({
					appArea: this.getQueryParam('area') ?? null,
					initialPath: prefix,
					country: mainStore.getCountryCode,
					domain: this.hostname,
				})

				this.appAreaConfig = appAreaConfig
				this.initialPath = prefix
			}
		},
	},

	persist: [
		sharedKeyPersist('vuex', 'location', weeklyStorage),
		sharedKeyPersist('auth', 'location', permanentStorage, ['initialUtmParams']),
	],
})
